import React from 'react';

import {observer} from 'mobx-react';

import {useStores} from '../../../Hooks';
import LimitedTextField from '../../Shared/LimitedTextField';
import ConditionalComponent from '../../Shared/ConditionalComponent';
import NameField from '../SettingsShared/NameField';
import SettingsThumbnail from '../SettingsShared/SettingsThumbnail';
import StyledOptionSelect from '../SettingsShared/StyledOptionSelect';
import ProjectPrivacySection from './ProjectPrivacySection';
import OwnersSection from './OwnersSection';

const labelClass = 'block mb-1 text-[11px] font-semibold text-main-normal tracking-[0.5px]';
const optionalLabelClass = 'text-main-hover font-normal';
const textareaClass = 'w-full block bg-transparent border-0 outline-none resize-none p-0 text-sm text-main-normal box-border';

const DescriptionField = observer(({value, onChange, originalValue}) => (
    <div>
        <label className={labelClass}>DESCRIPTION <span className={optionalLabelClass}>(optional)</span></label>
        <div className="settings-input-wrap">
            <LimitedTextField
                data-testid="project-description-input"
                className={textareaClass}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                minRows={3}
                maxRows={5}
                multiline
                isTextAreaAutoSize
                originalValue={originalValue}
                limit={2000}
            />
        </div>
    </div>
));

const NONE_OPTION = {value: 'none', title: 'None'};

const Default3DModelField = observer(({value, onChange, threeDModels}) => {
    const options = [
        NONE_OPTION,
        ...threeDModels.map((m) => ({value: m.ProcedureIdentifier, title: m.ProcedureName})),
    ];
    return (
        <div className="flex items-start justify-between gap-3">
            <div>
                <label className={labelClass}>DEFAULT DIGITAL TWIN</label>
                <p className="text-xs text-main-hover">Automatically connects to new procedures</p>
            </div>
            <StyledOptionSelect
                value={value || 'none'}
                onChange={(v) => onChange(v === 'none' ? null : v)}
                options={options}
            />
        </div>
    );
});

const ProjectInfoTab = observer(({settings, threeDModels}) => {
    const {generalStore} = useStores();
    const isAdminOrOwner = generalStore.userIsWorkspaceAdminOrOwner();
    const currentUserId = generalStore.userDetails?._id?.toString();
    const isCurrentUserProjectOwner = settings.editedProjectOwners.some((o) => String(o._id || o) === currentUserId);
    const canEditOwners = isAdminOrOwner || isCurrentUserProjectOwner;
    const isMirrored = settings.isMirrored;

    return (
        <div>
            <ConditionalComponent if={!isMirrored}>
                <div className="settings-section">
                    <div className="flex items-start gap-3">
                        <SettingsThumbnail imageSrc={settings.editedCoverImageThumb || settings.editedCoverImage} onChange={settings.updateCoverImage} />
                        <div className="flex-1 flex flex-col gap-2">
                            <NameField value={settings.editedName} onChange={settings.setEditedName} originalValue={settings.project?.ProjectName} limit={500} label="PROJECT NAME" data-testid="project-name-input" />
                            <ConditionalComponent if={canEditOwners}>
                                <OwnersSection owners={settings.editedProjectOwners} onChange={settings.setEditedProjectOwners} />
                            </ConditionalComponent>
                        </div>
                    </div>
                    <DescriptionField value={settings.editedDescription} onChange={settings.setEditedDescription} originalValue={settings.project?.ProjectDescription} />
                </div>
            </ConditionalComponent>

            <ConditionalComponent if={isAdminOrOwner}>
                <div className="settings-section">
                    <ProjectPrivacySection settings={settings} />
                </div>
            </ConditionalComponent>

            <ConditionalComponent if={!isMirrored}>
                <div className="settings-section">
                    <Default3DModelField value={settings.editedDefault3DModel} onChange={settings.setEditedDefault3DModel} threeDModels={threeDModels} />
                </div>
            </ConditionalComponent>
        </div>
    );
});

export default ProjectInfoTab;
